import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format, addDays, isSameDay } from "date-fns";
import { it } from "date-fns/locale";
import { Loader2, LogOut, RefreshCw, Trash2, ArrowLeft, CalendarDays, Clock, Ban, Lock, CalendarCheck, Unlink, Plus, Scissors, Pencil, X, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const isClosedDay = (d) => d.getDay() === 0 || d.getDay() === 1;
const DURATIONS = [20, 25, 30, 40, 45, 50, 60];

const endTime = (time, duration) => {
  const [h, m] = time.split(":").map(Number);
  const t = h * 60 + m + (duration || 60);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [calendarBusy, setCalendarBusy] = useState(false);
  const [aligning, setAligning] = useState(false);
  const [services, setServices] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [blockOpen, setBlockOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const panelDays = useMemo(() => {
    const days = Array.from({ length: 60 }, (_, i) => addDays(new Date(), i));
    if (editing) {
      const ed = new Date(editing.date + "T00:00:00");
      while (days[days.length - 1] < ed && days.length < 60) {
        days.push(addDays(days[days.length - 1], 1));
      }
    }
    return days;
  }, [editing]);
  const [blockDay, setBlockDay] = useState(() => panelDays.find((d) => !isClosedDay(d)) || panelDays[0]);
  const [blockSlots, setBlockSlots] = useState([]);
  const [blockClosed, setBlockClosed] = useState(false);
  const [blockTime, setBlockTime] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [blocking, setBlocking] = useState(false);

  const [addDay, setAddDay] = useState(() => panelDays.find((d) => !isClosedDay(d)) || panelDays[0]);
  const [addService, setAddService] = useState(null);
  const [addDuration, setAddDuration] = useState(45);
  const [addSlots, setAddSlots] = useState([]);
  const [addClosed, setAddClosed] = useState(false);
  const [addTime, setAddTime] = useState(null);
  const [addForm, setAddForm] = useState({ name: "", phone: "", notes: "" });
  const [adding, setAdding] = useState(false);

  const blockDateStr = format(blockDay, "yyyy-MM-dd");
  const addDateStr = format(addDay, "yyyy-MM-dd");

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (searchParams.get("calendar") === "connected") {
      toast.success("Google Calendar collegato: le nuove prenotazioni appariranno in automatico.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadBookings = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data.bookings);
    } catch (err) {
      if (err?.response?.status === 401) setUser(null);
      else if (!silent) toast.error("Impossibile caricare l'agenda.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCalendarStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/calendar/status");
      setCalendar(data);
    } catch {
      /* silenzioso */
    }
  }, []);

  const alignDurations = async () => {
    setAligning(true);
    try {
      const { data } = await api.post("/admin/bookings/align-durations");
      if (data.updated === 0) {
        toast.success("Agenda già allineata: nessuna durata da aggiornare.");
      } else {
        toast.success(`${data.updated} appuntamenti aggiornati ai nuovi tempi di taglio.`);
      }
      if (data.conflicts?.length) {
        toast.warning(
          `Attenzione: ${data.conflicts.length} possibili sovrapposizioni (es. ${data.conflicts[0].date}: ${data.conflicts[0].first} / ${data.conflicts[0].second}). Controlla l'agenda.`,
          { duration: 10000 }
        );
      }
      loadBookings(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAligning(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadBookings();
      loadCalendarStatus();
      api.get("/config").then(({ data }) => setServices(data.services)).catch(() => {});
      const t = setInterval(() => loadBookings(true), 30000);
      return () => clearInterval(t);
    }
  }, [user, loadBookings, loadCalendarStatus]);

  const loadBlockAvailability = useCallback(async () => {
    try {
      const { data } = await api.get("/availability", { params: { date: blockDateStr, duration: 60 } });
      setBlockSlots(data.slots);
      setBlockClosed(data.closed);
    } catch {
      toast.error("Impossibile caricare le fasce del giorno.");
    }
  }, [blockDateStr]);

  useEffect(() => {
    if (blockOpen && user) {
      setBlockTime(null);
      loadBlockAvailability();
    }
  }, [blockOpen, blockDay, user, loadBlockAvailability]);

  const loadAddAvailability = useCallback(async () => {
    try {
      const { data } = await api.get("/availability", { params: { date: addDateStr, duration: addDuration, overrun: 1 } });
      setAddSlots(data.slots.filter((s) => s.available));
      setAddClosed(data.closed);
    } catch {
      toast.error("Impossibile caricare gli orari liberi.");
    }
  }, [addDateStr, addDuration]);

  useEffect(() => {
    if (addOpen && user) {
      loadAddAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOpen, addDay, addDuration, user, loadAddAvailability]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      setUser(data);
      toast.success(`Bentornato, ${data.name.split(" ")[0]}.`);
    } catch (err) {
      toast.error(formatApiError(err, "Accesso non riuscito."));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setBookings([]);
  };

  const connectCalendar = async () => {
    setCalendarBusy(true);
    try {
      const { data } = await api.get("/oauth/calendar/connect");
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(formatApiError(err, "Impossibile avviare la connessione a Google."));
      setCalendarBusy(false);
    }
  };

  const disconnectCalendar = async () => {
    setCalendarBusy(true);
    try {
      await api.post("/admin/calendar/disconnect");
      setCalendar({ connected: false, email: null });
      toast.success("Google Calendar scollegato.");
    } catch {
      toast.error("Operazione non riuscita.");
    } finally {
      setCalendarBusy(false);
    }
  };

  const cancelBooking = async (id) => {
    setCancelling(id);
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success("Appuntamento rimosso: il tempo è di nuovo libero.");
    } catch {
      toast.error("Annullamento non riuscito.");
    } finally {
      setCancelling(null);
      setConfirmId(null);
    }
  };

  const blockOne = async (time) => {
    const { data } = await api.post("/admin/block-slot", {
      date: blockDateStr,
      time,
      reason: blockReason.trim() || null,
    });
    return data;
  };

  const blockSelected = async () => {
    if (!blockTime) return;
    setBlocking(true);
    try {
      await blockOne(blockTime);
      toast.success(`Fascia ${blockTime} bloccata per ${format(blockDay, "EEEE d MMMM", { locale: it })}.`);
      setBlockTime(null);
      loadBlockAvailability();
      loadBookings(true);
    } catch (err) {
      toast.error(formatApiError(err, "Blocco non riuscito."));
      loadBlockAvailability();
    } finally {
      setBlocking(false);
    }
  };

  const blockWholeDay = async () => {
    const free = blockSlots.filter((s) => s.available).map((s) => s.time);
    if (free.length === 0) return;
    setBlocking(true);
    let ok = 0;
    for (const t of free) {
      try {
        await blockOne(t);
        ok += 1;
      } catch {
        /* orario già occupato */
      }
    }
    toast.success(`Giornata bloccata (${ok} fasce): ${format(blockDay, "EEEE d MMMM", { locale: it })}.`);
    setBlocking(false);
    setBlockTime(null);
    loadBlockAvailability();
    loadBookings(true);
  };

  const pickAddService = (id) => {
    setAddService(id);
    if (id) {
      const s = services.find((x) => x.id === id);
      if (s) setAddDuration(s.duration);
    }
  };

  const resetAddPanel = () => {
    setEditing(null);
    setAddForm({ name: "", phone: "", notes: "" });
    setAddService(null);
    setAddDuration(50);
    setAddTime(null);
  };

  const startEdit = (b) => {
    setEditing(b);
    setBlockOpen(false);
    setAddOpen(true);
    setConfirmId(null);
    setAddForm({ name: b.blocked ? "" : b.name, phone: b.phone || "", notes: b.notes || "" });
    setAddService(services.some((s) => s.id === b.service) ? b.service : null);
    setAddDuration(b.duration || 60);
    setAddDay(new Date(b.date + "T00:00:00"));
    setAddTime(b.time);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addTime) {
      toast.error("Scegli un orario di inizio libero.");
      return;
    }
    setAdding(true);
    try {
      if (editing) {
        await api.put(`/admin/bookings/${editing.id}`, {
          name: editing.blocked ? "Fascia bloccata" : addForm.name.trim(),
          phone: addForm.phone.trim() || null,
          notes: addForm.notes.trim() || null,
          service: addService,
          date: addDateStr,
          time: addTime,
          duration: addDuration,
        });
        toast.success(`Appuntamento aggiornato: ${format(addDay, "EEEE d MMMM", { locale: it })} ore ${addTime}.`);
      } else {
        await api.post("/admin/appointments", {
          name: addForm.name.trim(),
          phone: addForm.phone.trim() || null,
          notes: addForm.notes.trim() || null,
          service: addService,
          date: addDateStr,
          time: addTime,
          duration: addDuration,
        });
        toast.success(`Appuntamento aggiunto: ${addForm.name.trim()} · ${format(addDay, "EEEE d MMMM", { locale: it })} ore ${addTime}.`);
      }
      resetAddPanel();
      setAddOpen(false);
      loadBookings(true);
    } catch (err) {
      toast.error(formatApiError(err, "Operazione non riuscita."));
      loadAddAvailability();
    } finally {
      setAdding(false);
    }
  };

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const realBookings = bookings.filter((b) => !b.blocked);
  const todayCount = realBookings.filter((b) => b.date === todayStr).length;
  const grouped = bookings.reduce((acc, b) => {
    (acc[b.date] = acc[b.date] || []).push(b);
    return acc;
  }, {});

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <Loader2 className="animate-spin text-ember" size={28} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center px-5 relative overflow-hidden">
        <div className="absolute -top-32 left-1/3 w-[500px] h-[400px] bg-ember/10 blur-[130px] rounded-full pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm bg-card border border-white/[0.08] rounded-lg p-8 relative"
        >
          <img src="/logo.png" alt="Modico Barberia" className="h-14 w-auto object-contain" />
          <h1 className="mt-5 font-serif text-3xl text-ivory">Area Riservata</h1>
          <p className="mt-2 text-sm text-stone">Agenda di Modico Barberia — accesso per Daniele.</p>
          <form onSubmit={login} className="mt-7 space-y-3">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
            />
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loggingIn}
              className="w-full py-3.5 bg-ember hover:bg-ember-hover disabled:opacity-50 text-white text-sm font-mono uppercase tracking-widest rounded-sm transition-colors duration-300 flex items-center justify-center gap-2"
            >
              {loggingIn && <Loader2 size={15} className="animate-spin" />}
              Entra
            </button>
          </form>
          <Link
            to="/"
            className="mt-6 flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-widest text-stone hover:text-sand transition-colors duration-300"
          >
            <ArrowLeft size={13} /> Torna al sito
          </Link>
        </motion.div>
      </div>
    );
  }

  const addSlotsShown = (() => {
    if (editing && addDateStr === editing.date && !addSlots.some((s) => s.time === editing.time)) {
      return [...addSlots, { time: editing.time, available: true }].sort((a, b) => a.time.localeCompare(b.time));
    }
    return addSlots;
  })();

  const dayChips = (selected, setSelected, prefix) => (
    <div className="grid sm:grid-cols-7 grid-cols-4 gap-2 max-h-56 overflow-y-auto no-scrollbar">
      {panelDays.map((d) => {
        const closedDay = isClosedDay(d);
        const active = isSameDay(d, selected);
        return (
          <button
            type="button"
            key={d.toISOString()}
            data-testid={`${prefix}-${format(d, "yyyy-MM-dd")}`}
            disabled={closedDay}
            onClick={() => setSelected(d)}
            className={`rounded-md border px-2 py-2.5 text-center transition-all duration-300 ${
              active
                ? "bg-ember border-ember text-white"
                : closedDay
                  ? "border-white/[0.05] text-stone/40 cursor-not-allowed"
                  : "border-white/10 text-sand hover:border-ember/50 hover:text-ivory"
            }`}
          >
            <span className="block text-[9px] font-mono uppercase tracking-widest">{format(d, "EEE", { locale: it })}</span>
            <span className="block font-serif text-lg leading-tight">{format(d, "d")}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-void">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-void/85 border-b border-white/[0.08]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 min-h-16 py-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Modico Barberia" className="h-9 w-auto object-contain" />
            <span className="font-serif text-lg text-ivory">
              Agenda<span className="hidden sm:inline"> — Modico Barberia</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="admin-add-appointment-button"
              onClick={() => { resetAddPanel(); setAddOpen((v) => !v); setBlockOpen(false); }}
              className={`flex items-center gap-2 px-3.5 sm:px-4 py-2.5 rounded-md border text-xs font-mono uppercase tracking-widest transition-colors duration-300 ${
                addOpen
                  ? "bg-ember border-ember text-white"
                  : "border-white/10 text-sand hover:text-ember hover:border-ember/40"
              }`}
            >
              <Plus size={14} /> Aggiungi
            </button>
            <button
              data-testid="admin-block-slot-button"
              onClick={() => { setBlockOpen((v) => !v); setAddOpen(false); resetAddPanel(); }}
              className={`flex items-center gap-2 px-3.5 sm:px-4 py-2.5 rounded-md border text-xs font-mono uppercase tracking-widest transition-colors duration-300 ${
                blockOpen
                  ? "bg-ember border-ember text-white"
                  : "border-white/10 text-sand hover:text-ember hover:border-ember/40"
              }`}
            >
              <Ban size={14} /> Blocca
            </button>
            <button
              data-testid="admin-align-durations-button"
              onClick={alignDurations}
              disabled={aligning}
              title="Allinea le durate degli appuntamenti futuri ai nuovi tempi dei servizi"
              className="flex items-center gap-2 px-3.5 sm:px-4 py-2.5 rounded-md border border-white/10 text-sand hover:text-ember hover:border-ember/40 disabled:opacity-50 text-xs font-mono uppercase tracking-widest transition-colors duration-300"
            >
              {aligning ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} <span className="hidden sm:inline">Allinea durate</span>
            </button>
            <button
              data-testid="admin-refresh-button"
              onClick={() => loadBookings()}
              className="p-2.5 rounded-md border border-white/10 text-sand hover:text-ember hover:border-ember/40 transition-colors duration-300"
              title="Aggiorna"
            >
              <RefreshCw size={15} />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 px-3.5 sm:px-4 py-2.5 rounded-md border border-white/10 text-sand hover:text-ivory hover:border-white/25 text-xs font-mono uppercase tracking-widest transition-colors duration-300"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <main data-testid="admin-dashboard-container" className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { l: "Appuntamenti oggi", v: todayCount },
            { l: "Prossimi appuntamenti", v: realBookings.length },
            { l: "Aggiornamento", v: "live 30s" },
          ].map((s) => (
            <div key={s.l} className="bg-card border border-white/[0.08] rounded-md p-5">
              <p className="font-serif text-3xl text-ember">{s.v}</p>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-stone">{s.l}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 bg-card border border-white/[0.08] rounded-md p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-10 h-10 shrink-0 rounded-sm border flex items-center justify-center ${
              calendar?.connected ? "bg-ember/10 border-ember/25 text-ember" : "bg-white/5 border-white/10 text-stone"
            }`}>
              <CalendarCheck size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-ivory font-medium">Google Calendar</p>
              <p data-testid="admin-calendar-status" className="text-xs text-stone truncate">
                {calendar === null
                  ? "Verifica connessione…"
                  : calendar.connected
                    ? `Collegato${calendar.email ? ` — ${calendar.email}` : ""}: prenotazioni, blocchi e annullamenti si sincronizzano da soli`
                    : calendar.expired
                      ? "Connessione scaduta: ricollega il calendario (i token Google in modalità test durano 7 giorni)"
                      : "Non collegato: collega il calendario per vedere le prenotazioni anche su Google"}
              </p>
            </div>
          </div>
          {calendar?.connected ? (
            <button
              data-testid="admin-calendar-disconnect-button"
              onClick={disconnectCalendar}
              disabled={calendarBusy}
              className="shrink-0 flex items-center gap-2 px-4 py-2.5 border border-white/10 text-stone hover:text-red-400 hover:border-red-400/40 text-xs font-mono uppercase tracking-widest rounded-sm transition-colors duration-300"
            >
              {calendarBusy ? <Loader2 size={13} className="animate-spin" /> : <Unlink size={13} />} Scollega
            </button>
          ) : (
            <button
              data-testid="admin-calendar-connect-button"
              onClick={connectCalendar}
              disabled={calendarBusy || calendar === null}
              className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-ember hover:bg-ember-hover disabled:opacity-50 text-white text-xs font-mono uppercase tracking-widest rounded-sm transition-colors duration-300"
            >
              {calendarBusy ? <Loader2 size={13} className="animate-spin" /> : <CalendarCheck size={13} />} Connetti
            </button>
          )}
        </div>

        {calendar?.connected && calendar?.last_error && (
          <div data-testid="admin-calendar-sync-error" className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3">
            <p className="text-xs text-amber-300 leading-relaxed">
              Ultima sincronizzazione fallita: {calendar.last_error}. Prova a scollegare e ricollegare il calendario.
            </p>
          </div>
        )}

        {addOpen && (
          <motion.form
            data-testid="admin-add-panel"
            onSubmit={submitAdd}
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 bg-card border border-ember/30 rounded-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-ember">
                {editing ? <Pencil size={15} /> : <Plus size={15} />}
                <span className="text-xs font-mono uppercase tracking-[0.25em]">
                  {editing ? `Modifica: ${editing.blocked ? "fascia bloccata" : editing.name} · ${editing.date} ${editing.time}` : "Aggiungi appuntamento a mano"}
                </span>
              </div>
              {editing && (
                <button
                  type="button"
                  data-testid="admin-edit-cancel-button"
                  onClick={() => { resetAddPanel(); setAddOpen(false); }}
                  className="p-2 text-stone hover:text-ivory transition-colors duration-300"
                  title="Annulla modifica"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {!editing?.blocked && (
              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                <input
                  data-testid="admin-add-name-input"
                  required
                  minLength={2}
                  value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  placeholder="Nome cliente *"
                  className="bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
                />
                <input
                  data-testid="admin-add-phone-input"
                  value={addForm.phone}
                  onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                  placeholder="Telefono (facoltativo)"
                  className="bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
                />
                <input
                  data-testid="admin-add-notes-input"
                  maxLength={300}
                  value={addForm.notes}
                  onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                  placeholder="Note (facoltative)"
                  className="bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
                />
              </div>
            )}
            {editing?.blocked && (
              <input
                data-testid="admin-add-notes-input"
                maxLength={300}
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                placeholder="Motivo del blocco"
                className="mt-5 w-full bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
              />
            )}

            {!editing?.blocked && (
              <div className="mt-4">
                <div className="flex items-center gap-2 text-ember mb-2">
                  <Scissors size={13} />
                  <span className="text-[10px] font-mono uppercase tracking-widest">Servizio (facoltativo — imposta la durata in automatico)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="admin-add-service-none"
                    onClick={() => pickAddService(null)}
                    className={`rounded-full border px-4 py-2 text-xs font-mono tracking-wide transition-all duration-300 ${
                      addService === null
                        ? "bg-ember border-ember text-white"
                        : "border-white/10 text-sand hover:border-ember/50 hover:text-ivory"
                    }`}
                  >
                    Senza servizio
                  </button>
                  {services.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      data-testid={`admin-add-service-${s.id.replace(/_/g, "-")}`}
                      onClick={() => pickAddService(s.id)}
                      className={`rounded-full border px-4 py-2 text-xs font-mono tracking-wide transition-all duration-300 ${
                        addService === s.id
                          ? "bg-ember border-ember text-white"
                          : "border-white/10 text-sand hover:border-ember/50 hover:text-ivory"
                      }`}
                    >
                      {s.name} · {s.price}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-stone mr-1">Durata:</span>
              {DURATIONS.map((d) => (
                <button
                  type="button"
                  key={d}
                  data-testid={`admin-add-duration-${d}`}
                  onClick={() => setAddDuration(d)}
                  className={`rounded-full border px-4 py-2 text-xs font-mono tracking-wide transition-all duration-300 ${
                    addDuration === d
                      ? "bg-ember border-ember text-white"
                      : "border-white/10 text-sand hover:border-ember/50 hover:text-ivory"
                  }`}
                >
                  {d} min
                </button>
              ))}
            </div>

            <div className="mt-4">{dayChips(addDay, setAddDay, "admin-add-day")}</div>

            <div data-testid="admin-add-slot-grid" className="mt-4 flex flex-wrap gap-2 max-h-44 overflow-y-auto no-scrollbar">
              {addClosed ? (
                <p className="text-sm text-stone py-3">Giorno di chiusura.</p>
              ) : addSlotsShown.length === 0 ? (
                <p className="text-sm text-stone py-3">Nessuno spazio libero da {addDuration} minuti in questo giorno.</p>
              ) : (
                addSlotsShown.map((s) => (
                  <button
                    type="button"
                    key={s.time}
                    data-testid={`admin-add-slot-${s.time.replace(":", "")}`}
                    onClick={() => setAddTime(s.time)}
                    className={`rounded-md border px-3.5 py-2 font-mono text-sm tracking-wider transition-all duration-300 ${
                      addTime === s.time
                        ? "bg-ember border-ember text-white"
                        : "border-white/10 text-sand hover:border-ember/50 hover:text-ivory"
                    }`}
                  >
                    {s.time}
                  </button>
                ))
              )}
            </div>

            <button
              data-testid="admin-add-submit"
              type="submit"
              disabled={adding || !addTime || addClosed}
              className="mt-4 px-6 py-3 bg-ember hover:bg-ember-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-mono uppercase tracking-widest rounded-sm transition-colors duration-300 flex items-center gap-2"
            >
              {adding && <Loader2 size={13} className="animate-spin" />}
              {editing ? "Salva modifica" : "Aggiungi"} {addTime ? `· ${addTime}–${endTime(addTime, addDuration)}` : ""}
            </button>
            {editing && !editing.blocked && editing.email && (
              <p className="mt-3 text-[11px] text-stone">Se sposti giorno o ora, il cliente riceve un'email con il nuovo orario.</p>
            )}
          </motion.form>
        )}

        {blockOpen && (
          <motion.div
            data-testid="admin-block-panel"
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 bg-card border border-ember/30 rounded-lg p-6"
          >
            <div className="flex items-center gap-2 text-ember">
              <Lock size={15} />
              <span className="text-xs font-mono uppercase tracking-[0.25em]">Blocca un'ora — pausa, ferie, impegni</span>
            </div>

            <div className="mt-5">{dayChips(blockDay, setBlockDay, "admin-block-day")}</div>

            <div className="mt-4 flex flex-wrap gap-2 max-h-44 overflow-y-auto no-scrollbar">
              {blockClosed ? (
                <p className="text-sm text-stone py-3">Giorno di chiusura: nessuna fascia da bloccare.</p>
              ) : (
                blockSlots.map((s) => (
                  <button
                    key={s.time}
                    data-testid={`admin-block-slot-${s.time.replace(":", "")}`}
                    disabled={!s.available}
                    onClick={() => setBlockTime(s.time)}
                    className={`rounded-md border px-3.5 py-2 font-mono text-sm tracking-wider transition-all duration-300 ${
                      blockTime === s.time
                        ? "bg-ember border-ember text-white"
                        : s.available
                          ? "border-white/10 text-sand hover:border-ember/50 hover:text-ivory"
                          : "border-white/[0.05] text-stone/40 line-through cursor-not-allowed"
                    }`}
                  >
                    {s.time}
                  </button>
                ))
              )}
            </div>

            <input
              data-testid="admin-block-reason-input"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              maxLength={120}
              placeholder="Motivo (facoltativo): pausa pranzo, ferie, impegno…"
              className="mt-4 w-full bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
            />

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                data-testid="admin-block-confirm-button"
                onClick={blockSelected}
                disabled={!blockTime || blocking || blockClosed}
                className="px-6 py-3 bg-ember hover:bg-ember-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-mono uppercase tracking-widest rounded-sm transition-colors duration-300 flex items-center gap-2"
              >
                {blocking && <Loader2 size={13} className="animate-spin" />}
                Blocca {blockTime ? `dalle ${blockTime}` : "l'ora"}
              </button>
              <button
                data-testid="admin-block-day-button"
                onClick={blockWholeDay}
                disabled={blocking || blockClosed || blockSlots.every((s) => !s.available)}
                className="px-6 py-3 border border-ember/40 hover:bg-ember/10 disabled:opacity-40 disabled:cursor-not-allowed text-ember text-xs font-mono uppercase tracking-widest rounded-sm transition-colors duration-300"
              >
                Blocca tutto il giorno
              </button>
            </div>
          </motion.div>
        )}

        <div className="mt-10">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="animate-spin text-ember" size={24} />
            </div>
          ) : bookings.length === 0 ? (
            <div className="bg-card border border-white/[0.08] rounded-lg p-14 text-center">
              <CalendarDays size={28} className="mx-auto text-stone" />
              <p className="mt-4 font-serif text-2xl text-ivory">Agenda libera</p>
              <p className="mt-2 text-sm text-stone">Nessun appuntamento in arrivo. I nuovi appariranno qui in automatico.</p>
            </div>
          ) : (
            Object.keys(grouped).sort().map((date) => (
              <div key={date} className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="font-serif text-2xl text-ivory capitalize">
                    {format(new Date(date + "T00:00:00"), "EEEE d MMMM", { locale: it })}
                  </h2>
                  {date === todayStr && (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-ember bg-ember/10 border border-ember/25 rounded-full px-3 py-1">
                      Oggi
                    </span>
                  )}
                </div>
                <div data-testid="admin-bookings-table" className="bg-card border border-white/[0.08] rounded-lg divide-y divide-white/[0.06]">
                  {grouped[date].map((b) => (
                    <div key={b.id} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 px-5 py-4">
                      <div className="flex items-center gap-2 w-36 shrink-0">
                        <Clock size={14} className="text-ember" />
                        <span className="font-mono text-base text-ivory">{b.time}–{endTime(b.time, b.duration)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        {b.blocked ? (
                          <>
                            <p className="text-ivory font-medium flex items-center gap-2">
                              Fascia bloccata
                              <span className="text-[9px] font-mono uppercase tracking-widest text-ember bg-ember/10 border border-ember/25 rounded-full px-2 py-0.5">
                                Bloccato
                              </span>
                            </p>
                            <p className="text-xs text-stone mt-0.5">{b.notes || "Pausa / impegno"}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-ivory font-medium">{b.name}</p>
                            <p className="text-xs text-stone mt-0.5">
                              {b.service_name} · {b.duration || 60} min
                              {b.phone ? <> · <a href={`tel:${b.phone}`} className="text-sand hover:text-ember">{b.phone}</a></> : ""}
                              {b.email ? ` · ${b.email}` : ""}
                            </p>
                            {b.notes && <p className="text-xs text-stone italic mt-1">“{b.notes}”</p>}
                          </>
                        )}
                      </div>
                      {confirmId === b.id ? (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            data-testid={`admin-confirm-cancel-${b.id.slice(0, 8)}`}
                            onClick={() => cancelBooking(b.id)}
                            disabled={cancelling === b.id}
                            className="px-4 py-2 bg-red-500/90 hover:bg-red-500 text-white text-xs font-mono uppercase tracking-widest rounded-sm transition-colors duration-300 flex items-center gap-2"
                          >
                            {cancelling === b.id && <Loader2 size={12} className="animate-spin" />}
                            Conferma
                          </button>
                          <button
                            onClick={() => setConfirmId(null)}
                            className="px-4 py-2 border border-white/10 text-sand text-xs font-mono uppercase tracking-widest rounded-sm hover:border-white/25 transition-colors duration-300"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            data-testid={`admin-edit-booking-button-${b.id.slice(0, 8)}`}
                            onClick={() => startEdit(b)}
                            className="flex items-center gap-2 px-4 py-2 border border-white/10 text-stone hover:text-ember hover:border-ember/40 text-xs font-mono uppercase tracking-widest rounded-sm transition-colors duration-300"
                          >
                            <Pencil size={13} /> Modifica
                          </button>
                          <button
                            data-testid={`admin-cancel-booking-button-${b.id.slice(0, 8)}`}
                            onClick={() => setConfirmId(b.id)}
                            className="flex items-center gap-2 px-4 py-2 border border-white/10 text-stone hover:text-red-400 hover:border-red-400/40 text-xs font-mono uppercase tracking-widest rounded-sm transition-colors duration-300"
                          >
                            <Trash2 size={13} /> {b.blocked ? "Sblocca" : "Annulla"}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
